import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";

import { TherapistAPI, type Therapist } from "../../therapist";
import { AuthAPI, SignUpDTO } from "../infrastructure/api/auth.api";
import { auth } from "../infrastructure/firebase";

/** Register a therapist. Returns the new therapist and their MFA enrolment. */
export async function registerTherapist(data: SignUpDTO) {
    const { data: result } = await AuthAPI.signUp(data);
    return result;
}

/** Create a Firebase account. Firebase errors propagate as they are. */
export async function createAccount(email: string, password: string): Promise<void> {
    await createUserWithEmailAndPassword(auth, email, password);
}

/** Ask the backend to send the signed-in user the email verification link. */
export async function sendVerificationEmail(): Promise<void> {
    await AuthAPI.sendVerificationEmail(`${window.location.origin}/auth/action`);
}

/**
 * Have Firebase send the signed-in user a verification link that brings them
 * back to `continueUrl`. Best effort: a failure, or no signed-in user, is
 * silently ignored.
 */
export async function sendEmailVerificationLink(continueUrl: string): Promise<void> {
    if (!auth.currentUser) return;
    await sendEmailVerification(auth.currentUser, { url: continueUrl }).catch(() => undefined);
}

/** Send the welcome email. */
export async function sendWelcomeEmail(): Promise<void> {
    await AuthAPI.sendWelcomeEmail();
}

/** Check an invitation code: whether it is live and which sign-up it opens. */
export async function validateInvitation(token: string) {
    const { data } = await AuthAPI.validateInvitation(token);
    return data;
}

/**
 * Save the language and patient term picked by a therapist who has just
 * signed up with Google.
 * Returns the updated therapist.
 */
export async function saveNewTherapistPreferences(
    therapistId: string,
    preferences: Pick<Partial<Therapist>, "language" | "patient_term">,
) {
    const { data } = await TherapistAPI.update(therapistId, preferences);
    return data;
}

/** Turn on MFA for the signed-in user. Returns the updated therapist. */
export async function enableMfaForSignedInUser(totpCode: string) {
    if (!auth.currentUser) throw new Error("No user logged in");
    const { data } = await AuthAPI.mfaEnable({ totp_code: totpCode });
    // The session now carries the MFA claim only once the token refreshes.
    await auth.currentUser?.getIdToken(true);
    return data;
}
